import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { CreateMeasurementSeriesDialog } from "#src/batteries-included-components/Dialogs/CreateMeasurementSeriesDialog";
import { DeleteMeasurementSeriesDialog } from "#src/batteries-included-components/Dialogs/DeleteMeasurementSeriesDialog";
import { EditMeasurementSeriesDialog } from "#src/batteries-included-components/Dialogs/EditMeasurementSeriesDialog";
import { useListMeasurementSeries } from "#src/components/hooks/adapters/useMeasurementSeries";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { useStorageKey } from "#src/hooks/useStorageKey";
import {
  Button,
  DataTable,
  DataTablePanel,
  HeaderType,
  ItemAction,
} from "@validereinc/common-components";
import {
  DeviceMeasurementSeriesSchema,
  MeasurementSeriesType,
  SortDirection,
} from "@validereinc/domain";
import React, { useState } from "react";

export const MeasurementSeriesTablePanel = ({
  deviceId,
}: {
  deviceId?: string;
}) => {
  const [tableStateStorageKey] = useStorageKey(
    ["table-state"],
    ["measurement-series", "list"]
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "description",
      sortDirection: SortDirection.ASCENDING,
    },
    undefined,
    tableStateStorageKey
  );
  const [isCreateMeasurementSeriesOpen, setIsCreateMeasurementSeriesOpen] =
    useState(false);
  const [itemToDelete, setItemToDelete] =
    useState<MeasurementSeriesType | null>();
  const [itemToUpdate, setItemToUpdate] =
    useState<MeasurementSeriesType | null>();
  const [isDeviceWriteEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:devices",
    permissionQuery: "devices:write",
  });
  const [isDeviceDeleteEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:devices",
    permissionQuery: "devices:delete",
  });

  const params = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: { deviceId },
  };

  const { data, isLoading } = useListMeasurementSeries(params, {
    enabled: !!deviceId,
  });
  const items = data?.data ?? [];

  const headers: Array<HeaderType<MeasurementSeriesType>> = [
    {
      key: DeviceMeasurementSeriesSchema.keyof().Enum.description,
      label: "Description",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.TextCell value={item.description} />
      ),
    },
    {
      key: DeviceMeasurementSeriesSchema.keyof().Enum.measurement_type,
      label: "Measurement Type",
    },
    {
      key: DeviceMeasurementSeriesSchema.keyof().Enum.unit,
      label: "Measurement Unit",
    },
  ];

  const getItemActions = ({
    item,
  }: {
    item: MeasurementSeriesType;
  }): ItemAction[] => [
    ...(isDeviceWriteEnabled
      ? ([
          {
            label: "Edit",
            buttonProps: {
              onClick: () => setItemToUpdate(item),
              icon: "pencil-simple",
            },
          },
        ] satisfies ItemAction[])
      : []),
    ...(isDeviceDeleteEnabled
      ? ([
          {
            label: "Delete",
            buttonProps: {
              onClick: () => setItemToDelete(item),
              icon: "trash",
            },
          },
        ] satisfies ItemAction[])
      : []),
  ];

  const actionRow = isDeviceWriteEnabled
    ? [
        <Button
          key="add-measurement-series"
          onClick={() => setIsCreateMeasurementSeriesOpen(true)}
        >
          Create
        </Button>,
      ]
    : [];

  return (
    <>
      <DataTablePanel
        panelProps={{ title: "Measurement Series", actionRow }}
        dataTableProps={{
          intent: "simplicity-first",
          items,
          isLoading,
          headers,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          getItemActions,
          getItemId: (item) => item.id,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
        }}
      />
      {deviceId && (
        <CreateMeasurementSeriesDialog
          id={deviceId}
          isOpen={isCreateMeasurementSeriesOpen}
          onClose={() => setIsCreateMeasurementSeriesOpen(false)}
        />
      )}

      {itemToUpdate && (
        <EditMeasurementSeriesDialog
          itemToUpdate={itemToUpdate}
          isOpen={!!itemToUpdate}
          onClose={() => setItemToUpdate(undefined)}
        />
      )}

      {itemToDelete && (
        <DeleteMeasurementSeriesDialog
          itemToDelete={itemToDelete}
          isOpen={!!itemToDelete}
          onClose={() => setItemToDelete(undefined)}
        />
      )}
    </>
  );
};
